import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect } from 'react'
import { ConnectionType } from '~/constants/connections'
import { DefaultVersion, JavaVersion, MavenVersion, MuleRuntimeVersion } from '~/constants/settings'
import { getIndexes } from '~/services/organizationService'
import { QueryKeys } from '~/services/queryClient'
import { SocketEvent } from '~/services/socket'
import { Index, IndexStatus } from '~/types/Indexes'
import { IndexRuntimeSettings } from '~/types/ResponseType'
import { AppEvents, globalEventEmitter } from '~/utils/shared'
import { useUserContext } from './UserContext'
import { useWorkspaceContext } from './WorkspaceContext'

export interface FlatIndex extends Index {
  index_name: string
  index_branch: string
  repo_name: string
  org_name: string
  branch_name: string
  java_version: JavaVersion
  maven_version: MavenVersion
  mule_runtime_version: MuleRuntimeVersion
  settings_xml_raw_txt: string
  settings_security_xml_raw_txt: string
  vm_args: string
}

function flatten(indexes: Index[]): FlatIndex[] {
  return indexes.map(index => {
    const runtimeSetting = index.indexer_metadata
      ? index.indexer_metadata.runtime_setting
      : {
          java_version: DefaultVersion.javaVersion,
          maven_version: DefaultVersion.mavenVersion,
          mule_runtime_version: DefaultVersion.muleRuntimeVersion,
          settings_xml_raw_txt: '',
          settings_security_xml_raw_txt: '',
          vm_args: ''
        }
    const isSelfHostedGithub = index.conn.connection_metadata?.is_self_hosted
    const normalizedConnType = isSelfHostedGithub
      ? ConnectionType.SelfHostedGithub
      : index.conn.conn_type

    return {
      ...index,
      conn: {
        ...index.conn,
        conn_type: normalizedConnType
      },
      index_name: index.name.name,
      index_branch: index.name.branch,
      org_name: index.name.name.split('/').slice(0, -1).join('/'),
      repo_name: index.name.name.split('/').at(-1) ?? '',
      branch_name: index.name.branch,
      java_version: runtimeSetting.java_version,
      maven_version: runtimeSetting.maven_version,
      mule_runtime_version: runtimeSetting.mule_runtime_version,
      settings_xml_raw_txt: runtimeSetting.settings_xml_raw_txt,
      settings_security_xml_raw_txt: runtimeSetting.settings_security_xml_raw_txt,
      vm_args: runtimeSetting.vm_args ?? ''
    } as FlatIndex
  })
}

type IndexRuntimeSettingPatch = Pick<
  IndexRuntimeSettings,
  | 'java_version'
  | 'maven_version'
  | 'mule_runtime_version'
  | 'settings_xml_raw_txt'
  | 'settings_security_xml_raw_txt'
  | 'vm_args'
>

// Module-scoped so every consumer of `useIndexesContext` shares one dedupe set.
// A per-instance `useRef` would log once per mounted consumer (settings tab,
// sidebar, indexes list, ...) which produced the 4x-spam we observed.
const loggedFailures = new Set<string>()

interface IndexesValue {
  indexes: FlatIndex[]
  isLoading: boolean
  isFetching: boolean
  error: Error | null
  refetch: () => void
  updateIndexRuntimeSetting: (indexId: number, runtimeSetting: IndexRuntimeSettingPatch) => void
}

/**
 * Read indexes for a workspace. Pass `overrideWorkspaceUuid` to read for a
 * different workspace than the one in `WorkspaceContext` (super-admin views).
 *
 * Backed by React Query — the cache is shared across every consumer in the app,
 * so two components that call this hook with the same workspace see the same
 * data without coordinating through a Provider.
 */
export function useIndexesContext(overrideWorkspaceUuid?: string): IndexesValue {
  const { isAuthenticated } = useUserContext()
  const { currentWorkspace } = useWorkspaceContext()
  const workspaceUuid = overrideWorkspaceUuid ?? currentWorkspace?.uuid
  const queryClient = useQueryClient()

  const enabled = isAuthenticated && !!workspaceUuid
  const query = useQuery({
    queryKey: [...QueryKeys.Indexes, workspaceUuid],
    queryFn: () => getIndexes(workspaceUuid!).then(flatten),
    enabled,
    // Primary refresh path is the socket invalidator below (`useIndexesInvalidator`).
    // Disable mount/focus/reconnect refetch and stale-time expiry so navigation
    // between pages doesn't re-hit the API for unchanged data.
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    // Polling fallback while any index is mid-transition (Queued/Indexing). If the
    // socket push doesn't arrive (DB trigger gap, dropped connection, backgrounded
    // tab), this guarantees the Runtime column reflects FAILED/SUCCEEDED without
    // a manual refresh. Stops automatically once every row is terminal.
    refetchInterval: q => {
      const data = q.state.data as FlatIndex[] | undefined
      if (!data || data.length === 0) return false
      const hasActive = data.some(
        i => i.status === IndexStatus.INITIALIZED || i.status === IndexStatus.IN_PROGRESS
      )
      return hasActive ? 5000 : false
    }
  })

  const refetch = useCallback(() => {
    if (!enabled) return
    query.refetch()
  }, [enabled, query])

  // The Runtime column renders "Failed" for any failed index but doesn't surface
  // the reason — the indexer (github_poller.mark_index_as_bad) does persist
  // `details.reason` to the row, we just don't have a place to show it. At scale
  // a toast would be unusable (hundreds of repos can fail at once), so log to the
  // console grouped by failure batch. Dedupe across the whole app session via the
  // module-scoped `loggedFailures` set so multiple consumers don't each re-log.
  useEffect(() => {
    const data = query.data
    if (!data || data.length === 0) return
    const newlyFailed = data.filter(i => {
      if (i.status !== IndexStatus.FAILED) return false
      const key = `${i.id}::${i.details?.reason ?? ''}`
      if (loggedFailures.has(key)) return false
      loggedFailures.add(key)
      return true
    })
    if (newlyFailed.length === 0) return
    /* eslint-disable no-console */
    console.groupCollapsed(
      `[Indexes] ${newlyFailed.length} repo${newlyFailed.length === 1 ? '' : 's'} failed to index`
    )
    newlyFailed.forEach(i => {
      console.warn(
        `${i.org_name}/${i.repo_name}/${i.branch_name}:`,
        i.details?.reason ??
          '(no reason recorded — check github-poller logs for index_id=' + i.id + ')'
      )
    })
    console.groupEnd()
    /* eslint-enable no-console */
  }, [query.data])

  const updateIndexRuntimeSetting = useCallback(
    (indexId: number, runtimeSetting: IndexRuntimeSettingPatch) => {
      queryClient.setQueryData<FlatIndex[]>([...QueryKeys.Indexes, workspaceUuid], prev =>
        prev?.map(index => {
          if (index.id !== indexId) return index
          return {
            ...index,
            ...runtimeSetting,
            indexer_metadata: {
              ...(index.indexer_metadata ?? {
                runtime_setting: runtimeSetting,
                is_internal_uploaded_index: false
              }),
              runtime_setting: {
                ...(index.indexer_metadata?.runtime_setting ?? {}),
                ...runtimeSetting
              }
            }
          }
        })
      )
    },
    [queryClient, workspaceUuid]
  )

  return {
    indexes: query.data ?? [],
    isLoading: enabled && query.isLoading,
    // isFetching is true during background refetches too (not just first load).
    // The list needs this to show a loading state during the refetch right after
    // a bulk save, when query.data is still the previous (often empty) set.
    isFetching: enabled && query.isFetching,
    error: (query.error as Error | null) ?? null,
    refetch,
    updateIndexRuntimeSetting
  }
}

/**
 * One-shot setup that bridges socket events and the global `IndexesUpdated`
 * event into React Query cache invalidation. Mount once at the authenticated
 * shell level — every `useIndexesContext` consumer shares the same cache, so
 * a single invalidator keeps them all in sync.
 */
export function useIndexesInvalidator(): void {
  const queryClient = useQueryClient()

  useEffect(() => {
    const onSocketEvent = (...args: unknown[]) => {
      const data = args[0] as { event: string }
      if (data.event === SocketEvent.IndexStatusUpdated) {
        globalEventEmitter.emit(AppEvents.IndexesUpdated)
      }
    }
    globalEventEmitter.on(AppEvents.SocketEvent, onSocketEvent)
    return () => {
      globalEventEmitter.off(AppEvents.SocketEvent, onSocketEvent)
    }
  }, [])

  useEffect(() => {
    const onIndexesUpdated = () => {
      queryClient.invalidateQueries({ queryKey: QueryKeys.Indexes })
    }
    globalEventEmitter.globalOn(AppEvents.IndexesUpdated, onIndexesUpdated)
    return () => {
      globalEventEmitter.off(AppEvents.IndexesUpdated, onIndexesUpdated)
    }
  }, [queryClient])
}
